import React, { useState, useEffect } from 'react';
import HRImages from '../Constant/HRImages';
import { FaCalendar, FaRegCalendar } from 'react-icons/fa';
import employeeAnalyticsService from '../Services/employeeAnalyticsService';

const UserMonthAnalysis = ({ employeeId }) => {

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; 
  const currentYear = currentDate.getFullYear();

  // State for selected month and year
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [monthlyData, setMonthlyData] = useState(null);


  const months = [
    { name: 'January', value: 1 },
    { name: 'February', value: 2 },
    { name: 'March', value: 3 },
    { name: 'April', value: 4 },
    { name: 'May', value: 5 },
    { name: 'June', value: 6 },
    { name: 'July', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'October', value: 10 },
    { name: 'November', value: 11 },
    { name: 'December', value: 12 },
  ].filter((month) => selectedYear < currentYear || month.value <= currentMonth);

 
  const fetchMonthlyData = async () => {
    try {
      const body = {
        userId: employeeId,
        year: selectedYear,
        month: selectedMonth, 
      };
      const response = await employeeAnalyticsService.getEmployeeMonthlySummary(body);
      setMonthlyData(response?.data);
    } catch (error) {
      console.error('Error fetching monthly data:', error);
    }
  };

  useEffect(() => {
    fetchMonthlyData();
  }, [employeeId, selectedMonth, selectedYear]);

  // Map JSON data to statsData
  const statsData = monthlyData
    ? [
        {
          id: 1,
          value: monthlyData.summary.daysPresent,
          label: 'Days Present',
          icon: HRImages.presentIcon,
          bgColor: 'bg-[#E8F5E8]',
          hoverBorder: 'hover:border-green-500',
          hoverBg: 'hover:bg-green-50',
          textColor: 'text-green-800',
        },
        {
          id: 2,
          value: monthlyData.summary.lateLogin,
          label: 'Late Login',
          icon: HRImages.lateIcon,
          bgColor: 'bg-[#FFF4E6]',
          hoverBorder: 'hover:border-orange-500',
          hoverBg: 'hover:bg-orange-50',
          textColor: 'text-orange-800',
        },
        {
          id: 3,
          value: monthlyData.summary.absent,
          label: 'Absent',
          icon: HRImages.absenceIcon,
          bgColor: 'bg-[#FFE6E6]',
          hoverBorder: 'hover:border-red-500',
          hoverBg: 'hover:bg-red-50',
          textColor: 'text-red-800',
        },
        {
          id: 4,
          value: monthlyData.summary.leave,
          label: 'Leave',
          icon: HRImages.leaveIcon,
          bgColor: 'bg-[#E6F3FF]',
          hoverBorder: 'hover:border-blue-500',
          hoverBg: 'hover:bg-blue-50',
          textColor: 'text-blue-800',
        },
        {
          id: 5,
          value: monthlyData.summary.overtime,
          label: 'Overtime',
          icon: HRImages.overtimeIcon,
          bgColor: 'bg-[#F0E6FF]',
          hoverBorder: 'hover:border-purple-500',
          hoverBg: 'hover:bg-purple-50',
          textColor: 'text-purple-800',
        },
        {
          id: 6,
          value: monthlyData.summary.permissionCount,
          label: 'Permission',
          icon: HRImages.permissionIcon,
          bgColor: 'bg-[#E6F0FF]',
          hoverBorder: 'hover:border-indigo-500',
          hoverBg: 'hover:bg-indigo-50',
          textColor: 'text-indigo-800',
        },
        {
          id: 7,
          value: monthlyData.summary.halfDay,
          label: 'Half Day',
          icon: HRImages.halfHourIcon,
          bgColor: 'bg-[#E8F8F5]',
          hoverBorder: 'hover:border-teal-500',
          hoverBg: 'hover:bg-teal-50',
          textColor: 'text-teal-800',
        },
        {
          id: 8,
          value: monthlyData.fineDetails.totalFineCount,
          label: 'Fine',
          icon: HRImages.fineIcon,
          bgColor: 'bg-[#FFF0F0]',
          hoverBorder: 'hover:border-red-500',
          hoverBg: 'hover:bg-red-50',
          textColor: 'text-red-600',
        },
      ]
    : [];


  const attendanceLogs = monthlyData
    ? monthlyData.attendanceLogs.map((log) => ({
        date: log.date,
        checkIn: log.checkIn,
        checkOut: log.checkOut,
        totalHours: log.totalHours,
        breaks: log.breaks,
        lateCheckIn: log.lateCheckIn,
        overtime: log.overtime,
        fine: log.fine || '--', 
        status: log.status,
        hasPermission: log.hasPermission || false, 
      }))
    : [];

  const getStatusBadge = (status, hasPermission) => {
    if (status === 'Present') {
      // return (
      //   <div className="flex items-center gap-2">
      //     <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">
      //       Present
      //     </span>
      //     {hasPermission && (
      //       <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
      //         Permission
      //       </span>
      //     )}
      //   </div>
      // );
      return <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded">Present</span>;
    } else if (status === 'Leave') {
      return <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded">Leave</span>;
    } else if (status === 'Absent') {
      return <span className="bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded">Absent</span>;
    } else if (status === 'Weekend') {
      return <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">Weekend</span>;
    }
    return null;
  };

  const getCellColor = (value, type) => {
    if (value === '--') return 'text-gray-400';
    switch (type) {
      case 'lateCheckIn':
        return value !== '--' ? 'text-orange-600 font-medium' : 'text-gray-400';
      case 'overtime':
        return value !== '--' ? 'text-purple-600 font-medium' : 'text-gray-400';
      case 'fine':
        return value !== '--' ? 'text-red-600 font-medium' : 'text-gray-400';
      default:
        return 'text-gray-900';
    }
  };

  return (
    <div className="min-h-screen rounded-lg bg-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <FaCalendar className="text-blue-600 text-xl" />
          </div>
          <h1 className="text-xl font-medium text-gray-900">This Month Analysis</h1>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border">
          <span className="text-gray-600">📅</span>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="font-medium bg-transparent outline-none"
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.name}
              </option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="font-medium bg-transparent outline-none"
          >
          
            {Array.from({ length: currentYear - 2020 + 1 }, (_, i) => 2020 + i).map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
        {statsData.map((stat) => (
          <div
            key={stat.id}
            className={`rounded-xl flex items-center gap-3 p-4 text-center shadow-sm border transition-all duration-300 cursor-pointer ${stat.hoverBorder} ${stat.hoverBg}`}
          >
            <div className={`${stat.bgColor} rounded-full text-2xl p-2`}>
              <img src={stat.icon} alt={stat.label} className="mx-auto h-5 w-5" />
            </div>
            <div className="flex flex-col gap-1">
              <div className={`text-lg font-bold ${stat.textColor}`}>{stat.value}</div>
              <div className="text-[11px] text-nowrap text-gray-600 font-medium">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid  gap-4 mb-8">

        <div
         className={`rounded-xl flex items-center gap-3 p-4 text-center shadow-sm border transition-all duration-300 cursor-pointer  hover:border-red-500 hover:bg-red-50`}

        >
            Fine Amount: <span className="font-semibold text-red-600"> {monthlyData ? monthlyData.fineDetails.totalFineAmount : '0'}</span>
        </div>
        
      </div>

      {/* Attendance Logs Section */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="flex items-center gap-3 p-6 border-b">
          <div className="bg-green-100 p-2 rounded-lg">
            <FaRegCalendar className="text-green-600 text-xl" />
          </div>
          <h2 className="text-lg text-gray-900">Attendance Logs</h2>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-700">Date</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Check In</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Check Out</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Total Hours</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Breaks</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Late Check In</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Overtime</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Fine</th>
                <th className="text-center px-4 py-4 text-sm font-medium text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {attendanceLogs.map((log, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{log.date}</td>
                  <td className="px-4 py-4 text-center text-sm text-gray-900">{log.checkIn}</td>
                  <td className="px-4 py-4 text-center text-sm text-gray-900">{log.checkOut}</td>
                  <td className="px-4 py-4 text-center text-sm text-gray-900">{log.totalHours}</td>
                  <td className="px-4 py-4 text-center text-sm text-gray-900">{log.breaks}</td>
                  <td className={`px-4 py-4 text-center text-sm ${getCellColor(log.lateCheckIn, 'lateCheckIn')}`}>
                    {log.lateCheckIn}
                  </td>
                  <td className={`px-4 py-4 text-center text-sm ${getCellColor(log.overtime, 'overtime')}`}>
                    {log.overtime}
                  </td>
                  <td className={`px-4 py-4 text-center text-sm ${getCellColor(log.fine, 'fine')}`}>
                    {log.fine}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {getStatusBadge(log.status, log.hasPermission)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UserMonthAnalysis;